import { useEffect, useState } from 'react';
import {
    SimpleGrid, Card, Text, Group, Badge, Stack, Loader, Center, Alert, Anchor,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconChevronRight, IconTool } from '@tabler/icons-react';
import { authedFetch } from './session';

const STATUS_LABELS = {
    ordered: 'Besteld', in_build: 'In opbouw', delivered: 'Afgeleverd',
    in_service: 'In dienst', decommissioned: 'Uit dienst',
};

const DUE = {
    ok:      { color: 'green',  label: 'Onderhoud op tijd' },
    soon:    { color: 'yellow', label: 'Onderhoud binnenkort' },
    overdue: { color: 'red',    label: 'Onderhoud te laat' },
};

function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('nl-NL');
}

export default function FleetOverview({ onSelect }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await authedFetch('/support/my/ambulances');
                setItems(r.items || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <Center p="xl"><Loader /></Center>;
    if (error) return <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert>;
    if (items.length === 0) {
        return (
            <Alert color="yellow" icon={<IconAlertCircle size={18} />} title="Geen voertuigen">
                Er staan momenteel geen voertuigen op uw account. Neem contact op met onze service-afdeling.
            </Alert>
        );
    }

    return (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {items.map((a) => {
                const title = a.license_plate || a.known_as || `Voertuig #${a.id}`;
                const due = a.service_due;
                const dueCfg = due ? DUE[due.status] : null;
                return (
                    <Card
                        key={a.id}
                        withBorder
                        padding="md"
                        style={{ cursor: 'pointer' }}
                        onClick={() => onSelect(a.id)}
                    >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <div style={{ minWidth: 0 }}>
                                <Text fw={600} truncate>{title}</Text>
                                <Text size="sm" c="dimmed" truncate>
                                    {`${a.base_vehicle_make || ''} ${a.base_vehicle_model || ''}`.trim() || '—'}
                                    {a.base_vehicle_year ? ` · ${a.base_vehicle_year}` : ''}
                                </Text>
                            </div>
                            <IconChevronRight size={18} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                        </Group>

                        <Group gap="xs" mt="sm">
                            <Badge variant="light" color="gray">{STATUS_LABELS[a.status] || a.status}</Badge>
                            {dueCfg && (
                                <Badge variant="light" color={dueCfg.color} leftSection={<IconTool size={11} />}>
                                    {dueCfg.label}
                                </Badge>
                            )}
                        </Group>

                        <Stack gap={2} mt="sm">
                            {due?.next_due_date && (
                                <Text size="xs" c="dimmed">Volgende beurt: {fmtDate(due.next_due_date)}</Text>
                            )}
                            {a.delivery_date && (
                                <Text size="xs" c="dimmed">Afgeleverd: {fmtDate(a.delivery_date)}</Text>
                            )}
                        </Stack>

                        {Number(a.needs_attention) === 1 && (
                            <Alert color="orange" mt="sm" p="xs" icon={<IconAlertTriangle size={14} />}>
                                <Text size="xs">{a.attention_note || 'Aandacht vereist'}</Text>
                            </Alert>
                        )}

                        <Anchor component="span" size="xs" mt="sm" style={{ display: 'inline-block' }}>
                            Details bekijken →
                        </Anchor>
                    </Card>
                );
            })}
        </SimpleGrid>
    );
}
